/**
 * WhatsApp Integration Utilities for Nomu Marketing
 *
 * Contact Number: +20 109 631 2346
 * International Format: 201096312346
 */

// WhatsApp contact information
export const WHATSAPP_PHONE = '201096312346';

/**
 * Opens WhatsApp with a custom message
 * @param {string} packageName - Name of the package
 * @param {string} packageDetails - Optional details about the package
 */
export const openWhatsApp = (packageName = '', packageDetails = '') => {
  let message = 'مرحباً';

  if (packageName) {
    message += `\n\nأنا مهتم بـــ ${packageName}`;
  } else {
    message += '\n\nأرغب في الحصول على استشارة مجانية حول خدماتكم.';
  }

  if (packageDetails) {
    message += `\n\n${packageDetails}`;
  }

  message += '\n\nأرغب في معرفة السعر وتفاصيل أكثر.';

  const encodedMessage = encodeURIComponent(message);
  const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encodedMessage}`;

  window.open(url, '_blank', 'noopener,noreferrer');

  // Track analytics
  trackWhatsAppClick(packageName || 'general_inquiry');
};

/**
 * Opens WhatsApp with a general consultation message
 */
export const openWhatsAppConsultation = () => {
  const message = `مرحباً

أرغب في الحصول على استشارة مجانية حول الخدمات المناسبة لمشروعي.

شكراً لكم`;

  const encodedMessage = encodeURIComponent(message);
  const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encodedMessage}`;

  window.open(url, '_blank', 'noopener,noreferrer');
  trackWhatsAppClick('consultation');
};

/**
 * Opens WhatsApp with a price quote request
 */
export const openWhatsAppQuote = () => {
  const message = `مرحباً

أرغب في الحصول على عرض سعر مفصّل للخدمات.

تفاصيل المشروع:
_______________________

شكراً لكم`;

  const encodedMessage = encodeURIComponent(message);
  const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encodedMessage}`;

  window.open(url, '_blank', 'noopener,noreferrer');
  trackWhatsAppClick('quote_request');
};

/**
 * Track WhatsApp button clicks for analytics
 * @param {string} packageName - Name of the package or action
 */
export const trackWhatsAppClick = (packageName) => {
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('WhatsApp clicked:', packageName);
  }

  // Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'whatsapp_click', {
      package_name: packageName,
      event_category: 'engagement',
      event_label: packageName,
    });
  }

  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Contact', {
      content_name: packageName,
    });
  }
};

/**
 * Package-specific WhatsApp message templates
 */
export const packageMessages = {
  // Web Design Packages
  webDesignStart: `مرحباً

أنا مهتم بـــ باقة البداية لتصميم المواقع

تفاصيل الباقة:
- صفحة واحدة (Landing Page)
- تصميم متجاوب
- ربط واتساب
- سرعة محسّنة
- تسليم خلال 4 أيام

أرغب في معرفة السعر وتفاصيل أكثر.`,

  webDesignGrow: `مرحباً

أنا مهتم بـــ باقة النمو لتصميم المواقع

تفاصيل الباقة:
- موقع 3-5 صفحات
- تصميم احترافي مخصص
- استضافة مجانية سنة
- SEO أساسي
- ربط بريد إلكتروني
- Google Analytics
- دعم فني 30 يوم

أرغب في معرفة السعر والبدء.`,

  webDesignPremium: `مرحباً

أنا مهتم بـــ باقة الاحتراف لتصميم المواقع

تفاصيل الباقة:
- موقع كامل متعدد الصفحات
- تصميم UI/UX مخصص
- سرعة تحميل فائقة
- SEO متقدم
- استضافة + SSL سنة
- بريد احترافي
- دعم فني 6 شهور

أرغب في معرفة السعر وحجز استشارة.`,

  webDesignEcommerce: `مرحباً

أنا مهتم بـــ باقة المتجر الإلكتروني

تفاصيل الباقة:
- متجر احترافي متجاوب
- حتى 50 منتج
- بوابات دفع (مدى، Apple Pay، STC Pay)
- ربط شركات شحن
- لوحة تحكم سهلة
- دعم العربية والإنجليزية
- دعم فني 60 يوم

أرغب في معرفة السعر والتفاصيل.`,

  // Social Media Packages
  socialMediaStart: `مرحباً

أنا مهتم بـــ باقة الانطلاقة لإدارة السوشيال ميديا

تفاصيل الباقة:
- إدارة حساب واحد
- 8 منشورات شهرياً
- تصميمات جذابة
- كتابة محتوى
- جدولة المنشورات
- رد على التعليقات

أرغب في معرفة السعر والبدء.`,

  socialMediaGold: `مرحباً

أنا مهتم بـــ الباقة الذهبية لإدارة السوشيال ميديا

تفاصيل الباقة:
- إدارة حسابين (Instagram + Facebook)
- 15 منشور شهرياً
- تصميمات احترافية
- جدولة ذكية
- رد على الرسائل
- 2 ستوري أسبوعياً
- تقرير تحليلي

أرغب في معرفة السعر وحجز استشارة.`,

  socialMediaPro: `مرحباً

أنا مهتم بـــ الباقة الاحترافية لإدارة السوشيال ميديا

تفاصيل الباقة:
- إدارة 3 منصات
- 25 منشور شهرياً
- محتوى متنوع (صور + فيديوهات)
- 4 فيديوهات قصيرة
- حملات تفاعل
- 5 ستوري أسبوعياً
- اجتماع شهري

أرغب في معرفة السعر والتفاصيل.`,

  // Paid Ads Packages
  paidAdsStart: `مرحباً

أنا مهتم بـــ باقة البداية للإعلانات الممولة

تفاصيل الباقة:
- حملة إعلانية واحدة
- منصة واحدة
- استهداف أساسي
- تصميم 2 إعلان
- إعداد البيكسل
- تقرير أسبوعي

أرغب في معرفة رسوم الإدارة والبدء.`,

  paidAdsGrow: `مرحباً

أنا مهتم بـــ باقة النمو للإعلانات الممولة

تفاصيل الباقة:
- حملتين إعلانيتين
- منصتين
- استهداف متقدم
- تصميم 5 إعلانات
- A/B Testing
- إعادة استهداف
- تقرير أسبوعي مفصّل

أرغب في معرفة رسوم الإدارة وحجز استشارة.`,

  paidAdsPro: `مرحباً

أنا مهتم بـــ باقة الاحتراف للإعلانات الممولة

تفاصيل الباقة:
- 3 حملات أو أكثر
- 3 منصات
- استهداف متقدم جداً
- تصميم 10 إعلانات
- فيديوهات إعلانية
- تحسين التحويلات
- اجتماع أسبوعي

أرغب في معرفة رسوم الإدارة وتفاصيل أكثر.`,
};

/**
 * Opens WhatsApp with a pre-defined message template
 * @param {string} messageKey - Key from packageMessages object
 */
export const openWhatsAppWithTemplate = (messageKey) => {
  const message = packageMessages[messageKey] || packageMessages.general || '';
  const encodedMessage = encodeURIComponent(message);
  const url = `https://wa.me/${WHATSAPP_PHONE}?text=${encodedMessage}`;

  window.open(url, '_blank', 'noopener,noreferrer');
  trackWhatsAppClick(messageKey);
};

/**
 * Contact information constants
 */
export const contactInfo = {
  phone: '+20 109 631 2346',
  phoneInternational: '201096312346',
  phoneDisplay: '+20 109 631 2346',
  email: 'contact@nomumarketing.com',
  website: 'www.nomumarketing.com',
  social: '@nomumarketing',
  workingHours: 'السبت - الخميس، 9:00 ص - 6:00 م (بتوقيت السعودية)',
  location: ' ',
  socialLinks: {
    facebook: 'https://www.facebook.com/share/1A47q66vSc/',
    instagram: 'https://www.instagram.com/nomumarketing1?igsh=NWtoN2h2NzRzbDM2',
    whatsapp: 'https://wa.me/+201096312346',
    snapchat: 'https://www.snapchat.com/add/nomu_marketing?share_id=XQpcHK04J7E&locale=en-US',
  },
};
